import posthog from "posthog-js";

const POSTHOG_KEY =
  import.meta.env.VITE_POSTHOG_KEY ?? "phc_Tn7VwYeF9rBKXaQSPwMd38ivoKB2r13tJYkOYWRkrMJ";

// VP-316: adblocki, Edge Tracking Prevention i filtrujące DNS ubijają żądania do
// *.i.posthog.com (ERR_BLOCKED_BY_CLIENT / ERR_TIMED_OUT) — u tych userów nie
// zbieraliśmy ani eventów, ani nagrań sesji. Na wdrożonych środowiskach ingest
// idzie więc przez własny CloudFront (behavior /ingest/* → PostHog), czyli
// first-party, którego blokery nie ruszają.
const POSTHOG_UI_HOST = "https://eu.posthog.com";
const POSTHOG_PROXY_PATH = "/ingest";
const LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1", "[::1]"]);

type PostHogHostInput = {
  hostname: string;
  origin: string;
  /** Nadpisanie z builda (VITE_POSTHOG_HOST) — ma pierwszeństwo nad resztą. */
  configured?: string;
};

/**
 * Adres, pod który leci ingest PostHoga (eventy, nagrania sesji, /static/ rrweb).
 *
 * Env var wygrywa, bo pozwala przepiąć ścieżkę bez zmiany kodu, gdyby `/ingest`
 * trafiło kiedyś na listy adblocków. Lokalnie proxy nie istnieje (CloudFront stoi
 * dopiero przed wdrożonym frontendem), więc tam lecimy prosto do PostHoga.
 */
export function resolvePostHogHost({ hostname, origin, configured }: PostHogHostInput): string {
  if (configured) return configured;
  if (LOCAL_HOSTNAMES.has(hostname)) return POSTHOG_UI_HOST;
  return `${origin}${POSTHOG_PROXY_PATH}`;
}

let initialized = false;

export function initPostHog(): void {
  if (typeof window === "undefined") return;
  if (initialized) {
    // VP-312: ponowna zgoda po wycofaniu — instancja żyje, ale siedzi w opt-out.
    posthog.opt_in_capturing();
    posthog.startSessionRecording();
    return;
  }

  posthog.init(POSTHOG_KEY, {
    api_host: resolvePostHogHost({
      hostname: window.location.hostname,
      origin: window.location.origin,
      configured: import.meta.env.VITE_POSTHOG_HOST,
    }),
    // Ingest leci przez naszą domenę, ale toolbar i linki mają prowadzić do UI PostHoga.
    ui_host: POSTHOG_UI_HOST,
    person_profiles: "identified_only",
    session_recording: {
      maskAllInputs: true,
      maskInputOptions: {
        password: true,
        email: false,
      },
    },
    capture_pageview: true,
    capture_pageleave: true,
    capture_exceptions: true,
    autocapture: {
      dom_event_allowlist: ["click", "submit", "change"],
    },
    loaded: () => {
      if (import.meta.env.DEV) {
        // eslint-disable-next-line no-console
        console.debug("[PostHog] loaded");
      }
    },
  });

  initialized = true;
}

/**
 * VP-312: wycofanie zgody analitycznej musi ZATRZYMAĆ PostHoga w tej samej sesji —
 * samo skasowanie ciasteczek nie wystarcza, bo persystencja siedzi też w localStorage,
 * a nagrywanie sesji leciałoby dalej do przeładowania strony.
 */
export function stopPostHog(): void {
  if (!initialized) return;

  posthog.stopSessionRecording();
  // Kolejność ma znaczenie: reset() czyści też zapisaną decyzję opt-out, więc gdyby
  // poszedł na końcu, PostHog wróciłby do stanu "pending" i zaczął zbierać od nowa.
  posthog.reset();
  posthog.opt_out_capturing();
}

export { posthog };
